import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
    Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Table, Tag, Timeline, Typography, message
} from 'antd';
import { LinkOutlined, PlusOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { api, asObj, AUDIT_ACTIONS, hasRole, ruDate, ruDateTime, showError, useSub } from './shared';

const { Text } = Typography;

/* ---------------- Исполнители ---------------- */

export const ExecutorsTab = () => {
    const { me, subcontractors, meta } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);
    const [history, setHistory] = useState(null);
    const [bulkSub, setBulkSub] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/executors');
            setRows(data);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const update = async (row, patch) => {
        try {
            await api.put(`/executors/${row.id}`, patch);
            message.success('Сохранено');
            load();
        } catch (error) {
            showError(error);
        }
    };

    const sync = async () => {
        try {
            const { data } = await api.post('/executors/sync', { months: 6 });
            message.success(`Найдено исполнителей в ВДР: ${data.found}, добавлено новых: ${data.created}`);
            load();
        } catch (error) {
            showError(error);
        }
    };

    const openHistory = async row => {
        try {
            const { data } = await api.get(`/executors/${row.id}/history`);
            setHistory({ row, items: data });
        } catch (error) {
            showError(error);
        }
    };

    const bulkBind = async () => {
        try {
            await api.post('/executors/bulk-bind', { ids: selected, subcontractor_id: bulkSub });
            message.success('Привязка сохранена');
            setSelected([]);
            load();
        } catch (error) {
            showError(error);
        }
    };

    const list = useMemo(() => {
        let r = rows;
        if (filter === 'unbound') r = r.filter(x => !x.subcontractor_id);
        if (filter === 'bound') r = r.filter(x => x.subcontractor_id);
        if (filter === 'inactive') r = r.filter(x => !x.active);
        if (search) {
            const q = search.toLowerCase();
            r = r.filter(x => `${x.fio} ${x.region || ''} ${x.subcontractor?.full_name || ''}`.toLowerCase().includes(q));
        }
        return r;
    }, [rows, filter, search]);

    const subOptions = subcontractors.filter(x => x.active).map(x => ({ value: x.id, label: x.short_name || x.full_name }));

    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <Select value={filter} onChange={setFilter} style={{ width: 200 }} options={[
                    { value: 'all', label: `Все (${rows.length})` },
                    { value: 'unbound', label: `Без субподрядчика (${rows.filter(x => !x.subcontractor_id).length})` },
                    { value: 'bound', label: 'Привязанные' },
                    { value: 'inactive', label: 'Неактивные' }
                ]} />
                <Input.Search allowClear placeholder="ФИО, регион, субподрядчик" style={{ width: 280 }} onSearch={setSearch} onChange={e => !e.target.value && setSearch('')} />
                <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
                {canEdit && <Button icon={<SyncOutlined />} onClick={sync}>Подтянуть исполнителей из ВДР</Button>}
                {canEdit && selected.length > 0 && (
                    <Space>
                        <Text>Выбрано: {selected.length}</Text>
                        <Select showSearch optionFilterProp="label" allowClear placeholder="Субподрядчик" style={{ width: 220 }} value={bulkSub} onChange={setBulkSub} options={subOptions} />
                        <Button type="primary" icon={<LinkOutlined />} onClick={bulkBind}>Привязать</Button>
                    </Space>
                )}
            </Space>
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={list}
                pagination={{ pageSize: 50, showTotal: t => `Всего: ${t}` }}
                rowSelection={canEdit ? { selectedRowKeys: selected, onChange: setSelected } : undefined}
                columns={[
                    { title: 'ФИО исполнителя', dataIndex: 'fio' },
                    {
                        title: 'Регион',
                        dataIndex: 'region',
                        width: 200,
                        render: (v, r) => (canEdit ? (
                            <Select size="small" allowClear showSearch style={{ width: 190 }} value={v || undefined} placeholder="—"
                                onChange={val => update(r, { region: val || null })}
                                options={(meta.regions || []).map(x => ({ value: x, label: x }))} />
                        ) : v)
                    },
                    {
                        title: 'Субподрядчик',
                        width: 260,
                        render: (_, r) => (canEdit ? (
                            <Select size="small" allowClear showSearch optionFilterProp="label" style={{ width: 250 }}
                                value={r.subcontractor_id || undefined} placeholder="не привязан"
                                status={r.subcontractor_id ? undefined : 'warning'}
                                onChange={val => update(r, { subcontractor_id: val || null })}
                                options={subOptions} />
                        ) : (r.subcontractor?.short_name || r.subcontractor?.full_name || <Text type="danger">не привязан</Text>))
                    },
                    {
                        title: 'Активен',
                        dataIndex: 'active',
                        width: 90,
                        render: (v, r) => (canEdit ? <Switch size="small" checked={v} onChange={val => update(r, { active: val })} /> : (v ? 'да' : 'нет'))
                    },
                    { title: 'Изменён', dataIndex: 'updated_at', width: 140, render: ruDateTime },
                    { title: '', width: 90, render: (_, r) => <Button size="small" type="link" onClick={() => openHistory(r)}>История</Button> }
                ]}
            />
            <Drawer open={!!history} onClose={() => setHistory(null)} width={560} title={`История: ${history?.row?.fio || ''}`}>
                <Timeline
                    items={(history?.items || []).map(h => {
                        const b = asObj(h.before);
                        const a = asObj(h.after);
                        return {
                            children: (
                                <div>
                                    <div><Text type="secondary">{ruDateTime(h.created_at)}</Text> · <b>{h.user_name || 'система'}</b></div>
                                    <div>{AUDIT_ACTIONS[h.action] || h.action}</div>
                                    {'subcontractor' in a && <div>Было: <b>{b.subcontractor || '—'}</b> → стало: <b>{a.subcontractor || '—'}</b></div>}
                                    {b.region !== a.region && 'region' in a && <div>Регион: {b.region || '—'} → {a.region || '—'}</div>}
                                    {b.active !== a.active && 'active' in a && <div>Активен: {String(b.active)} → {String(a.active)}</div>}
                                </div>
                            )
                        };
                    })}
                />
                {!history?.items?.length && <Text type="secondary">Изменений не было</Text>}
            </Drawer>
        </>
    );
};

/* ---------------- Субподрядчики ---------------- */

const SUB_FORM = [
    ['full_name', 'Полное название', 24, true],
    ['short_name', 'Краткое название', 12],
    ['code', 'Сокращение для номера акта', 12],
    ['inn', 'ИНН', 8],
    ['kpp', 'КПП', 8],
    ['ogrnip', 'ОГРНИП (для ИП)', 8],
    ['ogrnip_date', 'Дата ОГРНИП', 8, false, 'date'],
    ['region', 'Регион', 8],
    ['legal_address', 'Юридический адрес', 24],
    ['contract_name', 'Договор (название)', 12],
    ['contract_number', 'Номер договора', 6],
    ['contract_date', 'Дата договора', 6, false, 'date'],
    ['director', 'Руководитель (для юрлица)', 12],
    ['coefficient', 'Индивидуальный коэффициент (если отличается)', 12, false, 'number'],
    ['bank_name', 'Банк', 12],
    ['bik', 'БИК', 6],
    ['checking_account', 'Расчётный счёт', 12],
    ['correspondent_account', 'Корр. счёт', 12],
    ['act_template', 'Шаблон акта / примечание', 24]
];

export const SubcontractorsTab = ({ onChanged }) => {
    const { me, subcontractors } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const open = row => {
        setEditing(row || {});
        form.resetFields();
        form.setFieldsValue({
            ...(row || { active: true }),
            contract_date: row?.contract_date ? dayjs(row.contract_date) : null,
            ogrnip_date: row?.ogrnip_date ? dayjs(row.ogrnip_date) : null,
            coefficient: row?.coefficient != null ? Number(row.coefficient) : null
        });
    };

    const save = async values => {
        setSaving(true);
        try {
            const payload = {
                ...values,
                contract_date: values.contract_date ? values.contract_date.format('YYYY-MM-DD') : null,
                ogrnip_date: values.ogrnip_date ? values.ogrnip_date.format('YYYY-MM-DD') : null
            };
            if (editing?.id) await api.put(`/subcontractors/${editing.id}`, payload);
            else await api.post('/subcontractors', payload);
            message.success('Сохранено');
            setEditing(null);
            onChanged?.();
        } catch (error) {
            showError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {canEdit && <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 12 }} onClick={() => open(null)}>Добавить субподрядчика</Button>}
            <Table
                size="small"
                rowKey="id"
                dataSource={subcontractors}
                pagination={false}
                onRow={r => ({ onDoubleClick: () => canEdit && open(r) })}
                columns={[
                    { title: 'Название', dataIndex: 'full_name', render: (v, r) => <>{v}{r.short_name && <div><Text type="secondary">{r.short_name}</Text></div>}</> },
                    { title: 'Код', dataIndex: 'code', width: 80 },
                    { title: 'ИНН / КПП', width: 190, render: (_, r) => [r.inn, r.kpp].filter(Boolean).join(' / ') },
                    { title: 'Договор', render: (_, r) => [r.contract_name, r.contract_number && `№${r.contract_number}`, r.contract_date && `от ${ruDate(r.contract_date)}`].filter(Boolean).join(' ') },
                    { title: 'Регион', dataIndex: 'region', width: 150 },
                    { title: 'К', dataIndex: 'coefficient', width: 70, render: v => (v != null ? Number(v) : <Text type="secondary">общий</Text>) },
                    { title: 'Статус', dataIndex: 'active', width: 100, render: v => (v ? <Tag color="green">активен</Tag> : <Tag>неактивен</Tag>) },
                    ...(canEdit ? [{ title: '', width: 90, render: (_, r) => <Button size="small" type="link" onClick={() => open(r)}>Изменить</Button> }] : [])
                ]}
            />
            <Modal
                open={!!editing}
                width={820}
                title={editing?.id ? 'Субподрядчик' : 'Новый субподрядчик'}
                okText="Сохранить"
                confirmLoading={saving}
                onCancel={() => setEditing(null)}
                onOk={() => form.validateFields().then(save)}
            >
                <Text type="secondary">Удаление не предусмотрено — для вывода из работы снимите флаг «Активен». Все изменения пишутся в журнал.</Text>
                <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
                    <Row gutter={12}>
                        {SUB_FORM.map(([name, label, span, required, type]) => (
                            <Col span={span} key={name}>
                                <Form.Item name={name} label={label} rules={required ? [{ required: true, message: 'Обязательное поле' }] : undefined}>
                                    {type === 'date' ? <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                                        : type === 'number' ? <InputNumber min={0.5} max={1.5} step={0.01} precision={4} style={{ width: '100%' }} />
                                            : <Input />}
                                </Form.Item>
                            </Col>
                        ))}
                        <Col span={24}>
                            <Form.Item name="active" label="Активен" valuePropName="checked"><Switch /></Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

/* ---------------- Сокращения ---------------- */

const KIND_TITLES = { contractor: 'Подрядчик', subcontractor: 'Субподрядчик', region: 'Регион' };

export const AbbreviationsTab = () => {
    const { me } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/abbreviations');
            setRows(data);
        } catch (error) {
            showError(error);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async values => {
        try {
            if (editing?.id) await api.put(`/abbreviations/${editing.id}`, values);
            else await api.post('/abbreviations', values);
            message.success('Сохранено');
            setEditing(null);
            load();
        } catch (error) {
            showError(error);
        }
    };

    return (
        <>
            <Space style={{ marginBottom: 12 }}>
                {canEdit && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing({}); form.resetFields(); form.setFieldsValue({ kind: 'region' }); }}>
                        Добавить
                    </Button>
                )}
                <Text type="secondary">Номер акта: ММ-Подрядчик-Субподрядчик-Регион, например 09-СКС-ВСК-ПРМ. Код субподрядчика также можно задать в его карточке.</Text>
            </Space>
            <Table
                size="small"
                rowKey="id"
                dataSource={rows}
                pagination={false}
                columns={[
                    { title: 'Тип', dataIndex: 'kind', width: 160, render: v => KIND_TITLES[v] || v },
                    { title: 'Полное название', dataIndex: 'full_name' },
                    { title: 'Сокращение', dataIndex: 'code', width: 140, render: v => <Tag color="blue">{v}</Tag> },
                    ...(canEdit ? [{
                        title: '',
                        width: 90,
                        render: (_, r) => <Button size="small" type="link" onClick={() => { setEditing(r); form.setFieldsValue(r); }}>Изменить</Button>
                    }] : [])
                ]}
            />
            <Modal open={!!editing} title="Сокращение" okText="Сохранить" onCancel={() => setEditing(null)} onOk={() => form.validateFields().then(save)}>
                <Form form={form} layout="vertical">
                    <Form.Item name="kind" label="Тип" rules={[{ required: true }]}>
                        <Select disabled={!!editing?.id} options={Object.entries(KIND_TITLES).map(([value, label]) => ({ value, label }))} />
                    </Form.Item>
                    <Form.Item name="full_name" label="Полное название (как в CRM)" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="code" label="Сокращение" rules={[{ required: true }]}><Input maxLength={10} /></Form.Item>
                </Form>
            </Modal>
        </>
    );
};
